
// RSI (Relative Strength Index) calculation
export function calculateRSI(prices, period = 14) {
  if (!prices || prices.length < period + 1) {
    // Return array of nulls if insufficient data
    return new Array(prices?.length || 0).fill(null);
  }

  const rsiValues = new Array(prices.length).fill(null);
  const gains = [];
  const losses = [];

  // Calculate initial gains and losses
  for (let i = 1; i < prices.length; i++) {
    const change = prices[i] - prices[i - 1];
    gains.push(change > 0 ? change : 0);
    losses.push(change < 0 ? Math.abs(change) : 0);
  }

  if (gains.length < period) {
    return rsiValues;
  }

  // Calculate initial averages using SMA
  let avgGain = gains.slice(0, period).reduce((sum, gain) => sum + gain, 0) / period;
  let avgLoss = losses.slice(0, period).reduce((sum, loss) => sum + loss, 0) / period;

  // Calculate first RSI value
  if (avgLoss === 0) {
    rsiValues[period] = 100;
  } else {
    const rs = avgGain / avgLoss;
    rsiValues[period] = 100 - (100 / (1 + rs));
  }

  // Calculate subsequent RSI values using exponential smoothing
  for (let i = period + 1; i < rsiValues.length; i++) {
    avgGain = ((avgGain * (period - 1)) + gains[i - 1]) / period;
    avgLoss = ((avgLoss * (period - 1)) + losses[i - 1]) / period;

    if (avgLoss === 0) {
      rsiValues[i] = 100;
    } else {
      const rs = avgGain / avgLoss;
      rsiValues[i] = 100 - (100 / (1 + rs));
    }
  }

  return rsiValues;
}

// EMA (Exponential Moving Average) calculation
export function calculateEMA(prices, period) {
  if (!prices || prices.length < period) {
    return new Array(prices?.length || 0).fill(null);
  }

  const emaValues = new Array(prices.length).fill(null);
  const multiplier = 2 / (period + 1);

  // Calculate initial SMA for first EMA value
  const smaSum = prices.slice(0, period).reduce((sum, price) => sum + price, 0);
  emaValues[period - 1] = smaSum / period;

  // Calculate subsequent EMA values
  for (let i = period; i < prices.length; i++) {
    emaValues[i] = (prices[i] * multiplier) + (emaValues[i - 1] * (1 - multiplier));
  }

  return emaValues;
}

// MACD (Moving Average Convergence Divergence) calculation
export function calculateMACD(prices, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
  if (!prices || prices.length < slowPeriod + signalPeriod) {
    const length = prices?.length || 0;
    return {
      macd: new Array(length).fill(null),
      signal: new Array(length).fill(null),
      histogram: new Array(length).fill(null)
    };
  }

  // Calculate fast and slow EMAs
  const fastEMA = calculateEMA(prices, fastPeriod);
  const slowEMA = calculateEMA(prices, slowPeriod);

  // Calculate MACD line (fast EMA - slow EMA)
  const macdLine = prices.map((_, i) => {
    if (fastEMA[i] === null || slowEMA[i] === null) return null;
    return fastEMA[i] - slowEMA[i];
  });

  // Calculate signal line (EMA of MACD line)
  const macdValues = macdLine.filter(val => val !== null);
  const signalEMA = calculateEMA(macdValues, signalPeriod);
  
  // Map signal values back to original array length
  const signalLine = new Array(prices.length).fill(null);
  let signalIndex = 0;
  for (let i = 0; i < macdLine.length; i++) {
    if (macdLine[i] !== null) {
      signalLine[i] = signalEMA[signalIndex] || null;
      signalIndex++;
    }
  }

  // Calculate histogram (MACD - Signal)
  const histogram = macdLine.map((macd, i) => {
    if (macd === null || signalLine[i] === null) return null;
    return macd - signalLine[i];
  });

  return {
    macd: macdLine,
    signal: signalLine,
    histogram: histogram
  };
}

// Stochastic Oscillator calculation
export function calculateStochastic(highs, lows, closes, period = 14, smoothK = 3, smoothD = 3) {
  if (!highs || !lows || !closes || highs.length < period) {
    const length = closes?.length || 0;
    return {
      k: new Array(length).fill(null),
      d: new Array(length).fill(null)
    };
  }

  const rawK = new Array(closes.length).fill(null);

  // Calculate raw %K
  for (let i = period - 1; i < closes.length; i++) {
    const periodHigh = Math.max(...highs.slice(i - period + 1, i + 1));
    const periodLow = Math.min(...lows.slice(i - period + 1, i + 1));
    
    if (periodHigh === periodLow) {
      rawK[i] = 50; // Avoid division by zero
    } else {
      rawK[i] = ((closes[i] - periodLow) / (periodHigh - periodLow)) * 100;
    }
  }

  // Smooth %K using SMA
  const smoothedK = new Array(closes.length).fill(null);
  for (let i = period + smoothK - 2; i < closes.length; i++) {
    const kValues = rawK.slice(i - smoothK + 1, i + 1).filter(val => val !== null);
    if (kValues.length === smoothK) {
      smoothedK[i] = kValues.reduce((sum, val) => sum + val, 0) / smoothK;
    }
  }

  // Calculate %D (SMA of smoothed %K)
  const smoothedD = new Array(closes.length).fill(null);
  for (let i = period + smoothK + smoothD - 3; i < closes.length; i++) {
    const dValues = smoothedK.slice(i - smoothD + 1, i + 1).filter(val => val !== null);
    if (dValues.length === smoothD) {
      smoothedD[i] = dValues.reduce((sum, val) => sum + val, 0) / smoothD;
    }
  }

  return {
    k: smoothedK,
    d: smoothedD
  };
}

// Simple Moving Average (helper function)
export function calculateSMA(prices, period) {
  if (!prices || prices.length < period) {
    return new Array(prices?.length || 0).fill(null);
  }

  const smaValues = new Array(prices.length).fill(null);

  for (let i = period - 1; i < prices.length; i++) {
    const sum = prices.slice(i - period + 1, i + 1).reduce((acc, price) => acc + price, 0);
    smaValues[i] = sum / period;
  }

  return smaValues;
}

// Bollinger Bands calculation (bonus indicator)
export function calculateBollingerBands(prices, period = 20, stdDev = 2) {
  if (!prices || prices.length < period) {
    const length = prices?.length || 0;
    return {
      upper: new Array(length).fill(null),
      middle: new Array(length).fill(null),
      lower: new Array(length).fill(null)
    };
  }

  const sma = calculateSMA(prices, period);
  const upper = new Array(prices.length).fill(null);
  const lower = new Array(prices.length).fill(null);

  for (let i = period - 1; i < prices.length; i++) {
    const periodPrices = prices.slice(i - period + 1, i + 1);
    const mean = sma[i];
    
    // Calculate standard deviation
    const variance = periodPrices.reduce((sum, price) => sum + Math.pow(price - mean, 2), 0) / period;
    const standardDeviation = Math.sqrt(variance);

    upper[i] = mean + (standardDeviation * stdDev);
    lower[i] = mean - (standardDeviation * stdDev);
  }

  return {
    upper: upper,
    middle: sma,
    lower: lower
  };
}
